import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FileUpload, Textarea, Alert, Table, Switch, Badge, Select } from '@dreamtree-org/twreact-ui';
import { Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PageHeader, SectionCard, StatCard, Money } from '@/components';
import { useAppApi } from '@/lib/api';
import { useMutation, useQuery } from '@/lib/useQuery';
import { useProductTypes, useStoreCustomFields, customFieldsOf } from '@/lib/productTypes';
import { errorText } from '@/lib/format';
import { parseCsv, validateImport, templateFor, columnsFor, conditionsHelp, MAX_IMPORT_ROWS } from '../lib/csv';
import { maskSerial } from '../lib/serial';
import { rowsOf } from '../lib/catalog';
import NoProductTypes, { noTypesChosen } from '../components/NoProductTypes';
import VariantImport from '../components/VariantImport';
import ShopifyImport from '../components/ShopifyImport';
import { takesVariants } from '../lib/variantCsv';

/**
 * SELL-02 bulk import: parse and validate on the client (per-row errors,
 * duplicates in the file, SKUs already in the store), then submit the valid
 * rows. The submit is a fixture until the import endpoint exists.
 */
export default function InventoryImport() {
  const nav = useNavigate();
  const { fetchApi } = useAppApi();
  const submit = useMutation('merchantImportSubmit');
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  // Types with options (sizes, colours) may import one row per variant instead (MERCH-88).
  const [byVariant, setByVariant] = useState(false);
  // Migrating from Shopify (MERCH-90): its product export, mapped onto the store's types in a dry run.
  const [fromShopify, setFromShopify] = useState(false);
  const [done, setDone] = useState(null);
  // One template per product type (B11-P2): a file holds units of one type.
  const registry = useProductTypes();
  const storeTypes = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  // One type per file; until the merchant picks one, the store's first type (no default type, ADR 0009 §1).
  const [picked, setTypeKey] = useState('');
  const typeKey = picked || ((storeTypes.data || []).find((t) => t.enabled) || {}).key || '';
  // The store's own fields are extra columns (MERCH-86), headed by their key or their label.
  const customFields = useStoreCustomFields();
  const type = customFields.withFields(registry.byKey[typeKey] || null);
  const cols = columnsFor(type);
  const template = templateFor(type);

  const validate = async (csv) => {
    setDone(null);
    setCheckError('');
    setChecking(true);
    try {
      const table = parseCsv(csv);
      // Real uniqueness check: which of these SKUs already exist in this store?
      const header = (table[0] || []).map((h) => String(h).trim().toLowerCase());
      const col = header.indexOf('internal_sku');
      const skus = col === -1 ? [] : [...new Set(table.slice(1, MAX_IMPORT_ROWS + 1).map((r) => String(r[col] || '').trim()).filter(Boolean))];
      let existing = [];
      if (skus.length) {
        const res = await fetchApi('crud', { model: 'InventoryUnit', action: 'list', where: { internal_sku: skus }, select: ['internal_sku'], limit: skus.length }, false);
        existing = rowsOf(res).map((r) => r.internal_sku);
      }
      setResult(validateImport(table, { existingSkus: existing, type, conditions: registry.conditions }));
    } catch (ex) {
      setCheckError(errorText(ex));
      setResult(null);
    } finally {
      setChecking(false);
    }
  };

  const onFiles = async (list) => {
    setFiles(list);
    const f = list && list[0];
    if (!f) return;
    const content = await f.text();
    setText(content);
    validate(content);
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([template], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-import-${typeKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doSubmit = async () => {
    const valid = result.rows.filter((r) => r.payload).map((r) => r.payload);
    try {
      const res = await submit.run({ rows: valid });
      setDone(res.data);
    } catch {
      /* submit.error is shown */
    }
  };

  const shown = result ? (onlyErrors ? result.rows.filter((r) => r.errors.length) : result.rows) : [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Import inventory from CSV"
        subtitle={`Up to ${MAX_IMPORT_ROWS} units per file. Every row is checked before anything is saved.`}
        back={{ label: 'Products', href: '/products?tab=stock' }}
        actions={<Button variant="outline" leftIcon={<Download size={16} />} onClick={downloadTemplate} disabled={!type}>Download template</Button>}
      />

      {!noTypesChosen(storeTypes.data) ? (
        <Switch checked={fromShopify} onChange={(on) => { setFromShopify(on); setResult(null); }} rightLabel="From a Shopify product export" />
      ) : null}
      {fromShopify && !noTypesChosen(storeTypes.data) ? <ShopifyImport /> : null}
      {fromShopify && !noTypesChosen(storeTypes.data) ? null : (<>
      {!noTypesChosen(storeTypes.data) && takesVariants(type) ? (
        <Switch checked={byVariant} onChange={(on) => { setByVariant(on); setResult(null); }} rightLabel={`One row per size and colour (${type.plural.toLowerCase()} with options)`} />
      ) : null}
      {byVariant && takesVariants(type) && !noTypesChosen(storeTypes.data) ? (
        <>
          <Select
            label="What is in this file?"
            options={(storeTypes.data || []).filter((t) => t.enabled).map((t) => ({ value: t.key, label: t.plural }))}
            value={typeKey}
            onChange={(v) => { if (v) setTypeKey(v); }}
            closeOnSelect
          />
          <VariantImport key={typeKey} type={type} />
        </>
      ) : null}
      {byVariant && takesVariants(type) ? null : noTypesChosen(storeTypes.data) ? <NoProductTypes /> : (
      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="1. Choose a file" className="lg:col-span-2">
          <Select
            label="What is in this file?"
            options={(storeTypes.data || []).filter((t) => t.enabled).map((t) => ({ value: t.key, label: t.plural }))}
            value={typeKey}
            onChange={(v) => { if (v) { setTypeKey(v); setResult(null); } }}
            helperText="One product type per file. Download its template for the right columns."
            closeOnSelect
          />
          <FileUpload accept=".csv,text/csv" files={files} onFileSelect={onFiles} onFileRemove={() => { setFiles([]); setResult(null); setText(''); }} maxSize={2 * 1024 * 1024} />
          <Textarea label="…or paste CSV" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={cols.required.join(',')} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => validate(text)} loading={checking} disabled={!text.trim()}>Validate</Button>
            <Button variant="ghost" onClick={() => { setText(template); validate(template); }}>Try the sample</Button>
          </div>
        </SectionCard>
        <SectionCard title="Columns">
          <p className="text-sm"><span className="font-medium">Required:</span> {cols.required.join(', ')}</p>
          <p className="text-sm opacity-80"><span className="font-medium">Optional:</span> {cols.optional.join(', ')}</p>
          {type && type.serial === 'imei' ? <p className="text-xs opacity-70">serial is the device's 15-digit IMEI.</p> : null}
          {customFieldsOf(type).length ? (
            <p className="text-xs opacity-70">Your fields: {customFieldsOf(type).map((a) => `${a.key} (${a.label})`).join(', ')}. Head each column with its key or its name; yes/no fields take yes or no.</p>
          ) : null}
          <p className="text-xs opacity-60">Amounts are in rupees (price, mrp, acquisition_cost). {conditionsHelp(type, registry.conditions)} Serials are hashed on save and shown masked.</p>
        </SectionCard>
      </div>
      )}

      {checkError ? <Alert variant="error" title="Could not check SKUs">{checkError}</Alert> : null}

      </>)}
      {result && !fromShopify && !(byVariant && takesVariants(type)) ? (
        <SectionCard title="2. Review">
          {result.headerErrors.map((e) => <Alert key={e} variant={/ignored/.test(e) ? 'warning' : 'error'}>{e}</Alert>)}
          <div className="grid gap-3 grid-cols-3">
            <StatCard label="Rows" value={result.rows.length} />
            <StatCard label="Ready" value={result.validCount} icon={<CheckCircle2 size={18} />} />
            <StatCard label="With errors" value={result.errorCount} icon={<AlertTriangle size={18} />} />
          </div>
          {result.rows.length ? (
            <>
              <Switch checked={onlyErrors} onChange={setOnlyErrors} rightLabel="Show only rows with errors" />
              <Table
                data={shown}
                columns={[
                  { key: 'line', label: 'Line' },
                  { key: 'sku', label: 'SKU', render: (r) => r.data.internal_sku || '—' },
                  { key: 'model', label: 'Model', render: (r) => [r.data.brand, r.data.series, r.data.model_name].filter(Boolean).join(' ') || '—' },
                  { key: 'serial', label: 'Serial', render: (r) => <span className="font-mono text-sm">{r.data.serial ? maskSerial(r.data.serial) : '—'}</span> },
                  { key: 'grade', label: 'Grade', render: (r) => r.data.grade || '—', hideOnMobile: true },
                  { key: 'price', label: 'Price', render: (r) => (r.payload && r.payload.price_paise ? <Money paise={r.payload.price_paise} /> : r.data.price || '—'), hideOnMobile: true },
                  {
                    key: 'result', label: 'Result', wrap: true,
                    render: (r) => (r.errors.length ? (
                      <ul className="text-sm text-error-600 list-disc pl-4">{r.errors.map((e) => <li key={e}>{e}</li>)}</ul>
                    ) : <Badge variant="success">Ready</Badge>),
                  },
                ]}
                showSerial={false}
                withAction={false}
                showColumnPicker={false}
                showReloadButton={false}
                pagination
                pageSize={25}
                wrapCells
              />
            </>
          ) : null}
          {submit.error ? <Alert variant="error" title="Import failed">{submit.error}</Alert> : null}
          {done ? (
            <div className="space-y-3">
              <Alert variant={done.rejected ? 'warning' : 'success'} title={done.rejected ? 'Import finished with skipped rows' : 'Import complete'}>
                Batch {done.batch_id}: {done.accepted} unit{done.accepted === 1 ? '' : 's'} added to inventory
                {done.rejected ? `, ${done.rejected} rejected by the server` : ''}.
              </Alert>
              {done.errors && done.errors.length ? (
                <ul className="text-sm space-y-1">
                  {done.errors.map((e) => <li key={e.row}>Row {e.row}: {e.error}</li>)}
                </ul>
              ) : null}
              <Button variant="outline" onClick={() => nav('/products?tab=stock')}>View in Products</Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={doSubmit} loading={submit.loading} disabled={!result.validCount || result.headerErrors.some((e) => !/ignored/.test(e))}>
                Import {result.validCount} valid row{result.validCount === 1 ? '' : 's'}
              </Button>
              {result.errorCount ? <span className="text-sm opacity-70">{result.errorCount} row{result.errorCount === 1 ? '' : 's'} with errors will be skipped. Fix them and re-upload to include them.</span> : null}
            </div>
          )}
        </SectionCard>
      ) : null}
    </div>
  );
}
